import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getEmailStatus } from "@/lib/email-status.functions";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/emails")({
  component: EmailsPage,
});

const RANGES = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 168 },
  { label: "30d", hours: 720 },
];

function statusVariant(status: string) {
  if (status === "sent") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (status === "pending") return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  if (status === "suppressed" || status === "complained")
    return "bg-yellow-500/15 text-yellow-300 border-yellow-500/30";
  if (status === "dlq" || status === "failed" || status === "bounced")
    return "bg-red-500/15 text-red-300 border-red-500/30";
  return "bg-white/5 text-muted-foreground border-white/10";
}

function EmailsPage() {
  const [hours, setHours] = useState(168);
  const [template, setTemplate] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const fetchStatus = useServerFn(getEmailStatus);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["admin", "emails", hours, template, status],
    queryFn: () => fetchStatus({ data: { hours, template, status } }),
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-lg font-semibold">Email delivery status</h2>
          <p className="text-xs text-muted-foreground">
            Queued, sent, failed, and suppressed notifications from the transactional email queue.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded border border-white/10 bg-card/40 p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.hours}
                onClick={() => setHours(r.hours)}
                className={`px-2.5 py-1 text-xs rounded ${hours === r.hours ? "bg-primary/20 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Total" value={data?.stats.total ?? 0} />
        <Stat label="Sent" value={data?.stats.sent ?? 0} accent="text-emerald-300" />
        <Stat label="Queued" value={data?.stats.pending ?? 0} accent="text-amber-300" />
        <Stat label="Failed" value={data?.stats.failed ?? 0} accent="text-red-300" />
        <Stat label="Suppressed" value={data?.stats.suppressed ?? 0} accent="text-yellow-300" />
      </div>

      {data?.alerts && data.alerts.length > 0 && (
        <div className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
          <span className="font-semibold uppercase tracking-wide">Alerts last fired:</span>{" "}
          {data.alerts.map((a, i) => (
            <span key={a.alert_kind}>
              {i > 0 ? " · " : " "}
              <span className="font-mono">{a.alert_kind}</span> — {a.last_count} in{" "}
              {a.last_window_minutes}m at {new Date(a.last_fired_at).toLocaleString()}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger className="h-9 w-[200px]">
            <SelectValue placeholder="All templates" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All templates</SelectItem>
            {(data?.templates ?? []).map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[160px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="pending">Queued</SelectItem>
            <SelectItem value="dlq">Failed (DLQ)</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
            <SelectItem value="suppressed">Suppressed</SelectItem>
            <SelectItem value="complained">Complained</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <section className="rounded border border-white/10 bg-card/40">
        <div className="border-b border-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Recent emails {data ? `(${data.rows.length})` : ""}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Template</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>When</TableHead>
              <TableHead>Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.rows ?? []).map((r, i) => (
              <TableRow key={(r.message_id ?? "x") + i}>
                <TableCell className="font-mono text-xs">{r.template_name ?? "—"}</TableCell>
                <TableCell className="text-xs">{r.recipient_email ?? "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={statusVariant(r.status)}>{r.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </TableCell>
                <TableCell className="max-w-[280px] truncate text-xs text-red-300/80" title={r.error_message ?? ""}>
                  {r.error_message ?? ""}
                </TableCell>
              </TableRow>
            ))}
            {data && data.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                  No emails in this window.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded border border-white/10 bg-card/40">
          <div className="border-b border-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            By template
          </div>
          <Table>
            <TableHeader>
              <TableRow><TableHead>Template</TableHead><TableHead className="text-right">Count</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {(data?.byTemplate ?? []).map((b) => (
                <TableRow key={b.template}>
                  <TableCell className="font-mono text-xs">{b.template}</TableCell>
                  <TableCell className="text-right text-xs">{b.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>

        <section className="rounded border border-white/10 bg-card/40">
          <div className="border-b border-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Suppressed addresses ({data?.suppressed.length ?? 0})
          </div>
          <Table>
            <TableHeader>
              <TableRow><TableHead>Email</TableHead><TableHead>Reason</TableHead><TableHead>When</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {(data?.suppressed ?? []).map((s) => (
                <TableRow key={s.email}>
                  <TableCell className="text-xs">{s.email}</TableCell>
                  <TableCell><Badge variant="outline" className={statusVariant("suppressed")}>{s.reason}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {data && data.suppressed.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
                    No suppressed addresses.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="rounded border border-white/10 bg-card/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-xl font-semibold ${accent ?? "text-foreground"}`}>{value}</div>
    </div>
  );
}